import { useParams, Link } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { Header } from '../components/layout/Header';
import { ContentPreview } from '../components/content/ContentPreview';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { useWeekContent, useGenerateTopics, useGenerateOutline, useGenerateBlog, useGenerateSocial, useGenerateGraphics } from '../hooks/useContent';
import { useApproveStage } from '../hooks/useWorkflow';
import { ContentType, STAGE_LABELS } from '../types';
import { doc, onSnapshot } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from '../firebase';
import { Week } from '../types';

const CONTENT_ORDER: ContentType[] = [
  'topics',
  'outline',
  'blog',
  'social_posts',
  'linkedin_founder',
  'graphics_specs',
];

export function ContentReviewPage() {
  const { weekId } = useParams<{ weekId: string }>();
  const [week, setWeek] = useState<Week | null>(null);
  const { content, loading } = useWeekContent(weekId);
  const approveStage = useApproveStage();

  const generateTopics = useGenerateTopics(weekId ?? '');
  const generateOutline = useGenerateOutline(weekId ?? '');
  const generateBlog = useGenerateBlog(weekId ?? '');
  const generateSocial = useGenerateSocial(weekId ?? '');
  const generateGraphics = useGenerateGraphics(weekId ?? '');

  useEffect(() => {
    if (!weekId) return;
    const unsubscribe = onSnapshot(doc(db, 'weeks', weekId), (snap) => {
      if (snap.exists()) {
        setWeek({ id: snap.id, ...snap.data() } as Week);
      }
    });
    return unsubscribe;
  }, [weekId]);

  if (!weekId) return null;

  const handleGenerate = async () => {
    if (!week) return;
    const stage = week.stage;

    if (stage === 'topic_selection') {
      await generateTopics.mutateAsync(undefined);
    } else if (stage === 'outline' && week.topic) {
      await generateOutline.mutateAsync(week.topic);
    } else if (stage === 'content_generation' && week.topic && content['outline']) {
      await generateBlog.mutateAsync({ topic: week.topic, outline: content['outline'].content });
    } else if (stage === 'graphics' && week.topic && content['blog']) {
      await Promise.all([
        generateSocial.mutateAsync({ topic: week.topic, blogContent: content['blog'].content }),
        generateGraphics.mutateAsync({ topic: week.topic, blogTitle: week.topic.title }),
      ]);
    }
  };

  const isGenerating =
    generateTopics.isPending ||
    generateOutline.isPending ||
    generateBlog.isPending ||
    generateSocial.isPending ||
    generateGraphics.isPending;

  const pieceCount = Object.keys(content).length;

  return (
    <Layout>
      <Header
        title={week?.topic?.title ?? 'Content Review'}
        subtitle={week ? STAGE_LABELS[week.stage] : undefined}
        actions={
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="text-sm text-gray-500 hover:text-falcon-navy font-body">
              ← Dashboard
            </Link>
            {week && week.stage !== 'published' && (
              <>
                <Button
                  variant="secondary"
                  onClick={handleGenerate}
                  loading={isGenerating}
                  disabled={isGenerating}
                >
                  Generate {week.stage === 'topic_selection' ? 'Topics' : week.stage === 'outline' ? 'Outline' : week.stage === 'content_generation' ? 'Blog' : 'Social + Graphics'}
                </Button>
                {pieceCount > 0 && (
                  <Button
                    onClick={() => approveStage.mutateAsync(weekId)}
                    loading={approveStage.isPending}
                  >
                    Approve Stage
                  </Button>
                )}
              </>
            )}
          </div>
        }
      />

      {week && (
        <div className="mb-6 flex items-center gap-3">
          <Badge variant="info">{STAGE_LABELS[week.stage]}</Badge>
          <Badge
            variant={
              week.status === 'published'
                ? 'success'
                : week.status === 'needs_revision'
                ? 'error'
                : week.status === 'pending_approval'
                ? 'warning'
                : 'default'
            }
          >
            {week.status.replace('_', ' ')}
          </Badge>
          {week.topic?.primary_keyword && (
            <span className="text-xs text-gray-500 font-body">{week.topic.primary_keyword}</span>
          )}
        </div>
      )}

      {loading ? (
        <Card>
          <div className="text-center py-12 text-gray-400 font-body text-sm">
            Loading content...
          </div>
        </Card>
      ) : pieceCount === 0 ? (
        <Card>
          <div className="text-center py-12">
            <p className="text-gray-400 font-body text-sm mb-4">
              No content generated yet for this stage.
            </p>
            {week && week.stage !== 'published' && (
              <Button onClick={handleGenerate} loading={isGenerating}>
                Generate Now
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <div className="space-y-6">
          {CONTENT_ORDER.filter((type) => content[type]).map((type) => (
            <ContentPreview
              key={type}
              type={type}
              content={content[type].content}
              status={content[type].status}
              weekSlug={week?.topic?.slug ?? weekId}
            />
          ))}
        </div>
      )}
    </Layout>
  );
}
